import React, { useEffect, useState, ReactNode, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
}

/**
 * PageTransition component provides consistent animation effects for page transitions.
 * It can be wrapped around page content to provide a smooth fade-in effect.
 */
export const PageTransition: React.FC<PageTransitionProps> = ({ children, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const prevModalStateRef = useRef(false);
  
  // Check if a modal is open by looking for the modal-open class on body
  useEffect(() => {
    const checkModalState = () => {
      const isOpen = document.body.classList.contains('modal-open');
      setIsModalOpen(isOpen);
      
      // When closing a modal, we want to prevent any page transition effects
      if (prevModalStateRef.current && !isOpen) {
        // Coming from open modal to closed modal - skip any animations
        const transitionWrappers = document.querySelectorAll('.page-transition-wrapper');
        transitionWrappers.forEach(wrapper => {
          if (wrapper instanceof HTMLElement) {
            wrapper.style.animation = 'none';
            wrapper.style.opacity = '1';
            wrapper.style.transform = 'none';
            wrapper.style.transition = 'none';
          }
        });
      }
      
      prevModalStateRef.current = isOpen;
    };
    
    // Initial check
    checkModalState();
    
    // Set up a mutation observer to detect changes in the body class
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkModalState();
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  useEffect(() => {
    // Add global styles for animations if they don't exist yet
    if (!document.getElementById('page-transition-styles')) {
      const style = document.createElement('style');
      style.id = 'page-transition-styles';
      style.innerHTML = `
        @keyframes pageTransitionFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes pageTransitionScaleIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes pageTransitionSlideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes pageTransitionSlideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        .page-transition-wrapper {
          opacity: 0;
        }
        
        .page-transition-wrapper.visible {
          animation: pageTransitionFadeIn 0.4s ease-out forwards;
        }
        
        /* When modal is open, keep the page visible */
        body.modal-open .page-transition-wrapper {
          opacity: 1 !important;
          animation: none !important;
        }
        
        /* Also add a delay of 300ms before any transitions can happen after modal close */
        .page-transition-wrapper.visible.modal-was-open {
          animation: none !important;
          opacity: 1 !important;
          transition: none !important;
        }
        
        .page-transition-item-1 {
          opacity: 0;
        }
        
        .page-transition-item-1.visible {
          animation: pageTransitionFadeIn 0.5s ease-out 0.1s forwards;
        }
        
        .page-transition-item-2 {
          opacity: 0;
        }
        
        .page-transition-item-2.visible {
          animation: pageTransitionScaleIn 0.6s ease-out 0.2s forwards;
        }
        
        .page-transition-item-3 {
          opacity: 0;
        }
        
        .page-transition-item-3.visible {
          animation: pageTransitionSlideInRight 0.7s ease-out 0.3s forwards;
        }
        
        .page-transition-item-4 {
          opacity: 0;
        }
        
        .page-transition-item-4.visible {
          animation: pageTransitionSlideInLeft 0.7s ease-out 0.3s forwards;
        }
        
        /* Disable animations for modal content */
        #modal-portal-root .page-transition-wrapper,
        #modal-portal-root .page-transition-item-1,
        #modal-portal-root .page-transition-item-2,
        #modal-portal-root .page-transition-item-3,
        #modal-portal-root .page-transition-item-4 {
          opacity: 1;
          animation: none;
          transform: none;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Skip animation and set visible immediately if a modal is open
    if (isModalOpen) {
      setIsVisible(true);
      return;
    }
    
    // If we just closed a modal, skip animations
    if (prevModalStateRef.current) {
      setIsVisible(true);
      return;
    }
    
    // Short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [isModalOpen]);
  
  // Scroll to top when the page transitions, but only if no modal is open
  useEffect(() => {
    if (!isModalOpen && !prevModalStateRef.current) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, isModalOpen]);
  
  // Apply special classes when a modal is open to prevent blank pages
  const transitionClass = isModalOpen 
    ? 'page-transition-wrapper visible modal-open-page' 
    : prevModalStateRef.current 
      ? 'page-transition-wrapper visible modal-was-open'
      : `page-transition-wrapper ${isVisible ? 'visible' : ''}`;
  
  return (
    <div className={`${transitionClass} ${className}`}>
      {children}
    </div>
  );
};

/**
 * PageTransitionItem component for individual elements within a page that need staggered animations.
 * Use index to control the animation delay and style.
 */
export const PageTransitionItem: React.FC<{ 
  children: ReactNode; 
  index?: number;
  className?: string;
  delay?: number;
}> = ({ 
  children, 
  index = 1, 
  className = '',
  delay = 0
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Additional delay can be provided as a prop
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50 + delay);
    
    return () => clearTimeout(timer);
  }, [delay]);
  
  // Ensure index is between 1-4 for proper animation class
  const safeIndex = Math.min(Math.max(index, 1), 4);
  
  return (
    <div 
      className={`page-transition-item-${safeIndex} ${isVisible ? 'visible' : ''} ${className}`}
      style={delay ? { animationDelay: `${0.1 * index + delay}s` } : undefined}
    >
      {children}
    </div>
  );
};

/**
 * Higher Order Component to add page transition animations to any page component.
 * 
 * @param Component The page component to wrap with transition effects
 * @returns A new component wrapped with page transition effects
 */
export const withPageTransition = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const WithPageTransition: React.FC<P> = (props) => {
    return (
      <PageTransition>
        <Component {...props} />
      </PageTransition>
    );
  };

  // Set display name for better debugging
  const displayName = Component.displayName || Component.name || 'Component';
  WithPageTransition.displayName = `withPageTransition(${displayName})`;

  return WithPageTransition;
};

// Export default to maintain backward compatibility
export default withPageTransition; 